import { useEffect, useState } from 'react';
import { useFetchTransactionUnitPrice } from '../../../hooks/useFetchTransactionUnitPrice';
import { useTransaction } from '../../../context/TransactionContext';
import { useUser } from '../../../context/UserContext';
import { SystemConstants, UserType } from '../../../constants/constants';
import { handleHourPriceRounding } from '../../../constants/transactionUtility';
import { ITransaction, ITransactionUnitPrice } from '../../../interface/transaction';
import { ITransactionParameters } from '../../../interface/parameters';
import { TransactionUnitPriceSource } from '../../../constants/transaction';

interface UseUnitPriceFetchEnablingProps {
  transaction: ITransaction | null;
  parameters: ITransactionParameters | undefined;
}

/**
 * Determines when to enable server unit price fetching based on transaction and parameter conditions.
 * Returns a boolean indicating whether fetching should be enabled.
 */
const useUnitPriceFetchEnabling = ({ transaction, parameters }: UseUnitPriceFetchEnablingProps) => {
  const [enableUnitPriceFetching, setEnableUnitPriceFetching] = useState<boolean>(false);

  useEffect(() => {
    // Early guard: disable fetching if no matter ID
    if (!transaction?.matter?.id) {
      setEnableUnitPriceFetching(false);
      return;
    }

    // Check if transaction type is not required
    const transactionTypeIdNotRequired: boolean =
      !parameters?.transactionTypeInUse ||
      (!parameters.transactionTypeRequired && !transaction?.transactionType);

    // Enable fetching if transaction type is not required OR if transaction type exists
    if (transactionTypeIdNotRequired || transaction?.transactionType?.id) {
      setEnableUnitPriceFetching(true);
    } else {
      setEnableUnitPriceFetching(false);
    }
  }, [transaction?.matter, transaction?.transactionType, parameters]);

  return enableUnitPriceFetching;
};

interface UseServerUnitPriceProcessingProps {
  serverUnitPrice: ITransactionUnitPrice | null | undefined;
  isLoadingServerUnitPrice: boolean;
  matterTransactionParameterDetail: ITransactionParameters | undefined;
  parameters: ITransactionParameters | undefined;
  isTransactionTypeHourBased: boolean;
  transaction: ITransaction | null;
  updateTransaction: (transaction: ITransaction) => void;
  setUnitPrice: (value: string) => void;
  setBillableHours: (value: string) => void;
  setTotalPrice: (value: string) => void;
  setWorkedHours: (value: string) => void;
}

// Pure utility functions for price calculations
const calculateStandardPriceUpdates = (
  priceData: ITransactionUnitPrice,
  isTransactionTypeHourBased: boolean,
  transaction: ITransaction | null,
  paramsDetail: ITransactionParameters,
  parameters: ITransactionParameters | undefined,
) => {
  const { roundedAmount: serverPriceRounded, roundingApplied: serverPriceRoundingApplied } =
    handleHourPriceRounding('0', isTransactionTypeHourBased, transaction, paramsDetail, parameters);

  return {
    unitPrice: serverPriceRounded,
    totalPrice: priceData.standardPrice!.toString(),
    serverPriceRounded,
    serverPriceRoundingApplied,
    transactionUpdates: {
      totalPrice: Number(priceData.standardPrice),
    },
  };
};

const calculateUnitPriceUpdates = (
  priceData: ITransactionUnitPrice,
  isTransactionTypeHourBased: boolean,
  transaction: ITransaction | null,
  paramsDetail: ITransactionParameters,
  parameters: ITransactionParameters | undefined,
) => {
  const { roundedAmount: serverPriceRounded, roundingApplied: serverPriceRoundingApplied } =
    handleHourPriceRounding(
      priceData?.units // Here the billable quantity will only be overridden if the server returns a units value.
        ? priceData.units.toString()
        : (transaction?.billableQuantity?.toString() ?? '0'),
      isTransactionTypeHourBased,
      transaction,
      paramsDetail,
      parameters,
    );

  return {
    unitPrice: (priceData?.unitPrice ?? 0).toString(),
    serverPriceRounded,
    serverPriceRoundingApplied,
    transactionUpdates: {
      unitPrice: Number(priceData?.unitPrice ?? 0),
    },
  };
};

// Hook for applying common form and transaction updates
const useApplyPriceUpdates = (
  setUnitPrice: (value: string) => void,
  setBillableHours: (value: string) => void,
  setTotalPrice: (value: string) => void,
  setWorkedHours: (value: string) => void,
  updateTransaction: (transaction: ITransaction) => void,
  transaction: ITransaction | null,
) => {
  return (
    updates: {
      unitPrice?: string;
      totalPrice?: string;
      serverPriceRounded: string;
      serverPriceRoundingApplied: boolean;
      transactionUpdates: Partial<ITransaction>;
    },
    priceData: ITransactionUnitPrice,
  ) => {
    // restrictPriceUpdate marks hours owned by the source entity (e.g. worktime/task duration) that the price endpoint must never touch.
    const canOverrideHours = !transaction?.restrictPriceUpdate;

    // Update form state
    if (updates.unitPrice !== undefined) setUnitPrice(updates.unitPrice);
    if (updates.totalPrice !== undefined) setTotalPrice(updates.totalPrice);
    if (canOverrideHours) {
      setBillableHours(updates.serverPriceRounded);
      setWorkedHours(updates.serverPriceRounded);
    }

    // Update transaction context
    updateTransaction({
      ...transaction,
      roundingRuleApplied: updates.serverPriceRoundingApplied,
      ...(canOverrideHours && {
        billableQuantity: Number(updates.serverPriceRounded ?? 0),
        workedHours: Number(updates.serverPriceRounded ?? 0),
      }),
      priceSource: priceData?.source,
      ...updates.transactionUpdates,
    } as unknown as ITransaction);
  };
};

/**
 * Applies server unit price data to form fields and transaction context.
 * Handles price rounding logic, form state updates, and transaction context mutations.
 */
const useServerUnitPriceApplication = ({
  serverUnitPrice,
  isLoadingServerUnitPrice,
  matterTransactionParameterDetail,
  parameters,
  isTransactionTypeHourBased,
  transaction,
  updateTransaction,
  setUnitPrice,
  setBillableHours,
  setTotalPrice,
  setWorkedHours,
}: UseServerUnitPriceProcessingProps) => {
  const applyPriceUpdates = useApplyPriceUpdates(
    setUnitPrice,
    setBillableHours,
    setTotalPrice,
    setWorkedHours,
    updateTransaction,
    transaction,
  );

  useEffect(() => {
    // Early guard returns for missing prerequisites
    if (isLoadingServerUnitPrice) return;
    if (!serverUnitPrice) return;
    if (!matterTransactionParameterDetail) return;

    // Calculate and apply the appropriate price updates based on data structure
    if (serverUnitPrice?.standardPrice) {
      const updates = calculateStandardPriceUpdates(
        serverUnitPrice,
        isTransactionTypeHourBased,
        transaction,
        matterTransactionParameterDetail,
        parameters,
      );
      applyPriceUpdates(updates, serverUnitPrice);
      return;
    }
    if (serverUnitPrice?.source === TransactionUnitPriceSource.NOT_SET) {
      return;
    }
    const updates = calculateUnitPriceUpdates(
      serverUnitPrice,
      isTransactionTypeHourBased,
      transaction,
      matterTransactionParameterDetail,
      parameters,
    );
    applyPriceUpdates(updates, serverUnitPrice);
  }, [serverUnitPrice, isLoadingServerUnitPrice]);
};

interface UseServerUnitPriceHandlingProps {
  transactionDate: string;
  isTransactionTypeHourBased: boolean;
  matterTransactionParameterDetail: ITransactionParameters | undefined;
  parameters: ITransactionParameters | undefined;
  setUnitPrice: (value: string) => void;
  setBillableHours: (value: string) => void;
  setTotalPrice: (value: string) => void;
  setWorkedHours: (value: string) => void;
}

export const useServerUnitPriceHandling = ({
  transactionDate,
  isTransactionTypeHourBased,
  matterTransactionParameterDetail,
  parameters,
  setUnitPrice,
  setBillableHours,
  setTotalPrice,
  setWorkedHours,
}: UseServerUnitPriceHandlingProps) => {
  const { transaction, updateTransaction } = useTransaction();
  const { user, userDetails } = useUser();

  const enableUnitPriceFetching = useUnitPriceFetchEnabling({ transaction, parameters });
  const { data: serverUnitPrice, isLoading: isLoadingServerUnitPrice } =
    useFetchTransactionUnitPrice({
      matterId: transaction?.matter?.id ?? '',
      ...(transaction?.transactionType?.id
        ? { transactionTypeId: transaction?.transactionType?.id }
        : {}),
      ...(parameters?.transactionTypeInUse === false ||
      (parameters?.transactionTypeRequired === false && !transaction?.transactionType?.id)
        ? { unitId: SystemConstants.UNIT_HOUR }
        : {}),
      userId: user?.id ?? '',
      transactionDate: transaction?.transactionDate ?? transactionDate,
      enabled:
        enableUnitPriceFetching &&
        user?.id !== undefined &&
        transaction?.shouldLoadServerPrice &&
        userDetails?.userType !== UserType.RESTRICTED,
    });

  useServerUnitPriceApplication({
    serverUnitPrice,
    isLoadingServerUnitPrice,
    matterTransactionParameterDetail,
    parameters,
    isTransactionTypeHourBased,
    transaction,
    updateTransaction,
    setUnitPrice,
    setBillableHours,
    setTotalPrice,
    setWorkedHours,
  });

  return {
    serverUnitPrice,
    isLoadingServerUnitPrice,
    enableUnitPriceFetching,
  };
};
